import { ApolloError } from '@apollo/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  MarketingContentPreview,
  LinearProgress,
  LoadedContent,
} from '@occc-app/design-system';
import { AwardsListItemFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { AwardCatalogActionsContainer } from './AwardCatalogActionsContainer';
import { routeCreator } from '../utils';
import { statusBadge } from '../../../lib/publicationStatusBadge';

type AwardCatalogTableProps = {
  awards?: AwardsListItemFragment[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  push: (url: string) => void;
};

export const AwardCatalogTable = ({
  awards,
  loading,
  error,
  paginationProps,
  push,
}: AwardCatalogTableProps) => {
  const isUserPermittedToEditAward = useIsUserPermittedTo(Feature.AWARD_EDIT);
  const isUserPermittedToDeleteAward = useIsUserPermittedTo(Feature.AWARD_DELETE);

  const areActionsVisible = isUserPermittedToEditAward || isUserPermittedToDeleteAward;

  const renderData = (items: AwardsListItemFragment[]) => {
    const rows = items.map((item) => {
      const { id, name, deleted, fulfillmentCount, marketingContents } = item;
      const onClick = () => {
        push(routeCreator.details(id));
      };

      return (
        <TableRow key={id} onClick={onClick}>
          <TableCell sx={{ wordBreak: 'break-all' }}>{name || 'No name'}</TableCell>
          <TableCell>
            <MarketingContentPreview
              title={marketingContents[0]?.title || 'No Name Provided'}
              description={marketingContents[0]?.shortDescription || 'No Description Provided'}
              imageUrl={marketingContents[0]?.imageUrl ?? undefined}
            />
          </TableCell>
          <TableCell>{statusBadge(item.deleted, item.publicationStatus)}</TableCell>
          <TableCell align='right'>{fulfillmentCount || ''}</TableCell>
          {areActionsVisible && (
            <TableCell padding='button' align='right'>
              <AwardCatalogActionsContainer
                id={id}
                name={name || ''}
                deleted={deleted}
                isUserPermittedToEditAward={isUserPermittedToEditAward}
                isUserPermittedToDeleteAward={isUserPermittedToDeleteAward}
              />
            </TableCell>
          )}
        </TableRow>
      );
    });

    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Marketing Content</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Fulfillments</TableCell>
              {areActionsVisible && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<AwardsListItemFragment>
      error={error}
      loading={loading}
      items={awards}
      renderData={renderData}
    />
  );
};
